import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Clock, BookOpen, Eye } from 'lucide-react';

function MyClasses() {
  const navigate = useNavigate();

  const [classes] = useState([
    {
      id: 1,
      name: 'Mathematics - Grade 7',
      role: 'teacher',
      schedule: 'Mon, Wed, Fri 9:00 AM',
      students: 28,
      room: 'A-204',
    },
    {
      id: 2,
      name: 'Mathematics - Grade 8',
      role: 'teacher',
      schedule: 'Mon, Wed, Fri 11:00 AM',
      students: 26,
      room: 'A-204',
    },
    {
      id: 3,
      name: 'Mathematics - Grade 7 (Section B)',
      role: 'teacher',
      schedule: 'Tue, Thu 9:00 AM',
      students: 30,
      room: 'A-204',
    },
    {
      id: 4,
      name: 'Algebra - Grade 9',
      role: 'teaching_assistant',
      schedule: 'Tue, Thu 2:00 PM',
      students: 32,
      room: 'B-105',
    },
    {
      id: 5,
      name: 'Geometry - Grade 10',
      role: 'teaching_assistant',
      schedule: 'Wed 1:00 PM',
      students: 24,
      room: 'B-108',
    },
    {
      id: 6,
      name: 'Mathematics - Grade 6',
      role: 'substitute',
      schedule: 'Fri 10:00 AM',
      students: 25,
      room: 'A-101',
    },
    {
      id: 7,
      name: 'Advanced Calculus - Grade 12',
      role: 'observer',
      schedule: 'Thu 3:00 PM',
      students: 18,
      room: 'A-305',
    },
  ]);

  const getRoleBadge = (role) => {
    const roleConfig = {
      teacher: { label: 'Teacher', className: 'bg-blue-500 hover:bg-blue-600' },
      teaching_assistant: { label: 'Teaching Assistant', className: 'bg-green-500 hover:bg-green-600 text-white' },
      substitute: { label: 'Substitute', className: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500' },
      observer: { label: 'Observer', className: 'bg-purple-500 hover:bg-purple-600 text-white border-purple-500' },
    };

    const config = roleConfig[role] || roleConfig.teacher;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'observer':
        return <Eye className="w-5 h-5 text-purple-500" />;
      case 'teaching_assistant':
        return <Users className="w-5 h-5 text-green-500" />;
      case 'substitute':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <BookOpen className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleClassClick = (classItem) => {
    // Convert class name to URL-friendly slug
    const className = classItem.name.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '');
    navigate(`/teacher/classes/${className}`);
  };

  const groupedClasses = {
    teacher: classes.filter((c) => c.role === 'teacher'),
    teaching_assistant: classes.filter((c) => c.role === 'teaching_assistant'),
    substitute: classes.filter((c) => c.role === 'substitute'),
    observer: classes.filter((c) => c.role === 'observer'),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-2">My Classes</h1>
          <p className="text-slate-600">Overview of all your assigned classes and roles</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Teaching</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.teacher.length}</p>
                </div>
                <BookOpen className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Assisting</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.teaching_assistant.length}</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Substitute</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.substitute.length}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Observing</p>
                  <p className="text-3xl font-bold text-slate-800">{groupedClasses.observer.length}</p>
                </div>
                <Eye className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classItem) => (
            <Card
              key={classItem.id}
              className="hover:shadow-lg transition-shadow duration-200 cursor-pointer"
              onClick={() => handleClassClick(classItem)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getRoleIcon(classItem.role)}
                    <CardTitle className="text-lg">{classItem.name}</CardTitle>
                  </div>
                </div>
                <div className="flex items-center gap-2">{getRoleBadge(classItem.role)}</div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-slate-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{classItem.schedule}</span>
                  </div>
                  <div className="flex items-center text-slate-600">
                    <Users className="w-4 h-4 mr-2" />
                    <span>{classItem.students} students</span>
                  </div>
                  <div className="flex items-center text-slate-600">
                    <BookOpen className="w-4 h-4 mr-2" />
                    <span>Room {classItem.room}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MyClasses;
